import type { PropsWithChildren } from "react";
import Pagination from "@react/widget/Pagination";
import { ApolloClient, ApolloProvider, InMemoryCache, useQuery, gql } from "@apollo/client";
import { loadErrorMessages, loadDevMessages } from "@apollo/client/dev";
import { query } from "./query";
import Icon from "@react/widget/Icon";
import { useTranslatedRelative } from "@i18next/client/utils";

const ENDPOINT = import.meta.env.PUBLIC_ENDPOINT ?? "https://stg-cms.avepoint.com/api";
const TOKEN = import.meta.env.PUBLIC_TOKEN ?? "T6t9Fa11ouec5Kkc75VeaIaPTX0yA7-h";

loadDevMessages();
loadErrorMessages();

const cache = new InMemoryCache();
const client = new ApolloClient({
  uri: ENDPOINT,
  headers: {
    authorization: TOKEN ? `Bearer ${TOKEN}` : "",
  },
  cache: cache
});

interface Props extends PropsWithChildren {
  currentSite: Page.Site;
  relatedToSection?: string;
  title?: string | null;
  all: boolean;
  page: number;
  label: Record<string, any>;
  getPostsByPage: (page: number) => void;
}

function PostsContent({
  currentSite,
  relatedToSection,
  title,
  all,
  page,
  label,
  getPostsByPage,
}: Props) {
  const tp = useTranslatedRelative(currentSite.locale);
  const search = new URLSearchParams(currentSite.search);
  const limit = 9;
  const offset = all ? (page - 1) * limit : (page - 1) * limit + 3;
  const GET_Posts = gql(query);
  const { loading, error, data, refetch } = useQuery(GET_Posts, {
    variables: {
      site: currentSite.handle,
      relatedToSection: relatedToSection ?? ["blogCategories", "blogTags", "blogAuthors"],
      title,
      limit,
      offset,
    },
  });

  if (loading) return <p>Loading...</p>;
  if (error) return <p>Error: {error.message}</p>;
  if (search.has("refresh")) refetch();

  const count = all ? data?.entryCount : data?.entryCount - 3;
  const total = Math.ceil(count / limit);

  if (total > 0 && data?.entries?.length > 0) {
    return (
      <>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 justify-items-center [&>div]:max-w-sm [&>div]:w-full">
          {data.entries.map(({ title, url, uri, blogFeaturedImage }: Record<string, any>) => (
            <div
              key={title}
              className="flex bg-white flex-col text-left rounded-lg p-0 relative [&>img]:aspect-[16/9] items-stretch max-w-md transition duration-500 hover:shadow-lg [&:hover_img]:scale-110"
            >
              <a
                className="bg-gray-50 rounded-t-lg [&>img]:h-[218px] [&>img]:w-[384px] min-h-[218px] overflow-hidden"
                href={tp(uri)}
              >
                <img
                  className="rounded-t-lg w-full h-full object-cover transition duration-500"
                  src={
                    blogFeaturedImage?.[0]?.src ??
                    "https://assets.avepoint.com/upload/blog/featured-image/default-feature-image.png"
                  }
                  width={384}
                  height={218}
                  alt={title}
                />
              </a>
              <div className="flex flex-col justify-between h-full p-5 border-r border-b border-l rounded-b-lg relative min-h-[200px]">
                <div>
                  <h3 className="text-base md:text-xl">
                    <a
                      className="hover:text-primary transition duration-1000 line-clamp-3"
                      href={tp(uri)}
                      target="_self"
                    >
                      {title}
                    </a>
                  </h3>
                </div>
                <div className="mt-4">
                  <a
                    className="btn flex font-semibold transition duration-300 cursor-pointer [&>*]:align-middle bg-white text hover:text-primary-hover active:bg-white [&_.icon]:text-primary group/btn w-full sm:w-fit py-2 md:py-[13px] text-base rounded-full px-0 justify-start !pb-0"
                    href={tp(uri)}
                  >
                    <p className="h-full flex items-center justify-center">
                      <span className="btn-text">{label.read || "Read the Blog"}</span>
                      <Icon className="ml-5 transition-all duration-300 group-hover/btn:translate-x-2" name="arrow-right-long"/>
                    </p>
                  </a>
                </div>
              </div>
            </div>
          ))}
        </div>
        <Pagination
          label={{ prev: label.prev || "Prev", next: label.next || "Next" }}
          page={page}
          total={total}
          onChange={getPostsByPage}
        />
      </>
    );
  }

  return <p>{label.nodata || "No posts found."}</p>;
}

export default function GetPosts(props: Props) {
  return (
    <ApolloProvider client={client}>
      <PostsContent {...props} />
    </ApolloProvider>
  );
}